// Server-side entry point for the export renderer.
//
// The scheduled-export renderer (a Node Lambda in the reporting service) bundles
// this file and drives the same generators the browser's Export builder uses on
// download, so an emailed report and a downloaded one can never drift.
//
// The design keeps ALL export logic in JS, one source of truth:
//   - REQUEST_SPEC lists the exact /api/dashboard/data calls the builder fires.
//     The Lambda replays them (it already has the tenancy + a service token) and
//     hands back the raw JSON responses.
//   - assembleLive() reshapes those responses into the `live` object the section
//     resolvers read - lifted verbatim from the builder, so the server sees
//     precisely what the browser sees.
//   - renderDocument() assembles the selected sections and generates the file.
//
// No DOM, no React: downloadBlob and the component tree are unreachable here.
import { assembleSections, PROVIDED_TEMPLATES } from '../exportData';
import { generateDocument, documentFilename } from '../exportGenerators';

// Resolve a provided-template id (board-pack, exec-review, ...) to its layout.
// Saved templates live in the reporting service's own store and the sender
// passes their sections directly; provided templates are only defined here, so
// the renderer resolves them from the bundle. Returns null for an unknown id.
export function resolveProvidedTemplate(templateId) {
    const t = PROVIDED_TEMPLATES.find((p) => p.id === templateId);
    return t
        ? { sections: t.sections, drill: t.drill || [], format: t.format, period: t.period, title: t.title || t.name }
        : null;
}

// The 14 aggregations the builder pulls. `needs` mirrors the builder's own
// section->query gating so the Lambda can skip calls a given template doesn't
// use. Each entry: key (the live-object slot), the request body, and the section
// ids that require it. period/scope are merged in by the caller (they vary per
// run). DWM entries carry a `dwm` marker because they resolve against the
// dwm-api, not /dashboard/data.
export const REQUEST_SPEC = [
    { key: 'risk',         body: { metric: 'simulations', aggregation: 'risk' },                     needs: ['home.risk-dial', 'risk.methodology', 'risk.factors', 'text.implication'] },
    { key: 'benchmark',    body: { metric: 'simulations', aggregation: 'risk_benchmark' },           needs: ['home.risk-dial', 'text.implication'], feature: 'industryBenchmarking' },
    { key: 'riskTrend',    body: { metric: 'simulations', aggregation: 'risk_trend' },               needs: ['home.risk-trend'] },
    { key: 'summary',      body: { metric: 'simulations', aggregation: 'summary' },                  needs: ['home.metrics'] },
    { key: 'sims',         body: { metric: 'simulations', aggregation: 'sims' },                     needs: ['sims.metrics', 'sims.funnel', 'sims.by-sim'] },
    { key: 'trends',       body: { metric: 'simulations', aggregation: 'trends' },                   needs: ['sims.clicks'] },
    { key: 'simDevices',   body: { metric: 'simulations', aggregation: 'sim_device' },               needs: ['sims.device'] },
    { key: 'leagues',      body: { metric: 'simulations', aggregation: 'leagues' },                  needs: ['sims.dept'] },
    { key: 'courses',      body: { metric: 'courses',     aggregation: 'courses' },                  needs: ['courses.metrics', 'courses.by-course'] },
    { key: 'courseDetail', body: { metric: 'courses',     aggregation: 'course_detail', limit: 50 }, needs: ['courses.metrics', 'courses.outstanding'] },
    { key: 'riskUsers',    body: { metric: 'simulations', aggregation: 'users', limit: 100 },        needs: ['home.top-risk', 'text.implication'] },
    { key: 'dwmStats',     dwm: 'stats',   needs: ['dwm.summary'],  feature: 'darkWebMonitoring' },
    { key: 'dwmTrend',     dwm: 'trend',   needs: ['dwm.trend'],    feature: 'darkWebMonitoring' },
    { key: 'dwmByEmail',   dwm: 'byEmail', needs: ['dwm.by-email'], feature: 'darkWebMonitoring' },
];

const DWM_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDwmBucket(bucket, granularity) {
    const b = String(bucket || '');
    if (granularity === 'week') return b.replace(/^\d{4}-W?/, 'wk ');
    if (granularity === 'day') return b.slice(5);
    const m = parseInt(b.slice(5, 7), 10);
    return `${DWM_MONTHS[m - 1] || b} ${b.slice(2, 4)}`;
}

// Reshape the raw API responses into the `live` object the resolvers read.
// Lifted verbatim from ExportBuilderPage so the server produces the exact shape
// the browser does. `r` is keyed by REQUEST_SPEC.key, each value the raw JSON
// body of that call (equivalent to the browser's RTK query.data).
export function assembleLive(r = {}) {
    const dwmTrend = r.dwmTrend || {};
    const dwmEmails = r.dwmByEmail || {};
    return {
        risk: r.risk?.risk,
        benchmark: r.benchmark?.riskBenchmark,
        riskTrend: r.riskTrend?.riskTrend,
        summary: r.summary?.performance,
        sims: r.sims?.sims,
        trends: r.trends?.trends,
        simDevices: r.simDevices?.simDevices,
        leagues: r.leagues?.leagues,
        courses: r.courses?.courses,
        courseDetail: r.courseDetail?.courseDetail,
        riskUsers: r.riskUsers?.riskUsers,
        dwmStats: r.dwmStats,
        dwmTrend: (dwmTrend.points || []).map((p) => ({
            label: fmtDwmBucket(p.bucket, dwmTrend.granularity || 'month'),
            value: Number(p.mentions) || 0,
        })),
        dwmByEmail: [...(dwmEmails.users || [])]
            .sort((a, b) => (b.breaches ?? 0) - (a.breaches ?? 0))
            .map((u) => ({
                email: u.email,
                mentions: u.breaches ?? 0,
                withCreds: u.passwordAvailable ?? 0,
                latest: u.latestBreachAt
                    ? new Date(`${u.latestBreachAt}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                    : '-',
            })),
    };
}

// Which REQUEST_SPEC entries a section list actually needs, honouring the same
// feature gating the builder uses (DWM off -> skip the dwm calls). Lets the
// Lambda make only the calls a template requires instead of all 14.
export function requestsFor(sectionIds, { dwmOn = false, benchmarkOn = false } = {}) {
    const wanted = new Set(sectionIds || []);
    return REQUEST_SPEC.filter((spec) => {
        if (spec.feature === 'darkWebMonitoring' && !dwmOn) return false;
        if (spec.feature === 'industryBenchmarking' && !benchmarkOn) return false;
        return spec.needs.some((id) => wanted.has(id));
    });
}

// sections: ordered section-id list (a saved template's sections)
// drill:    ids that should include row-level detail (template's drill)
// live:     the assembled aggregation payload (from assembleLive)
// meta:     { brand, periodLabel, scopeLabel, period, startDate, endDate, benchmarkOn }
export async function renderDocument({ format, title, sections, drill, live, meta }) {
    const assembled = assembleSections(
        sections || [], drill || [],
        { period: meta?.period, startDate: meta?.startDate, endDate: meta?.endDate, benchmarkOn: meta?.benchmarkOn },
        live || null,
    );
    const { content, mime, filename } = await generateDocument(format, title, assembled, meta || {});
    return { content, mime, filename: filename || documentFilename(format, title) };
}
